
import { Settings, LogOut, BookOpen, Shield, Heart, ExternalLink, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getUserInitials } from "@/utils/i18n";
import { useLanguage } from "@/contexts/language-context";
import subManagerLogo from "@/assets/sub-manager-logo.png";

interface UserMenuProps {
  userEmail: string | null;
  handleLogout: () => Promise<void>;
  isParentView?: boolean;
  isDualRole?: boolean;
  managementRoleLabel?: string;
  hasTeams?: boolean;
}

export function UserMenu({
  userEmail,
  handleLogout,
  isParentView = false,
  isDualRole = false,
  managementRoleLabel = "Coach",
  hasTeams = true,
}: UserMenuProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const initials = getUserInitials(userEmail);
  const showApps = hasTeams && !isParentView;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="h-9 w-9 cursor-pointer border border-border hover:border-primary/50 transition-colors">
          <AvatarFallback className="bg-primary/20 text-primary dark:bg-primary/30 dark:text-primary-foreground font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {userEmail && (
          <>
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground truncate">
              {userEmail}
            </DropdownMenuLabel>
            {isDualRole && (
              <DropdownMenuLabel className="font-normal text-xs text-muted-foreground flex items-center gap-1 pt-0">
                {isParentView ? <Heart className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                {t('userMenu.viewingAs')}: {isParentView ? t('userMenu.parent') : managementRoleLabel}
              </DropdownMenuLabel>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
          <Settings className="me-2 h-4 w-4" />
          <span>{t('userMenu.settings')}</span>
        </DropdownMenuItem>
        {hasTeams && !isParentView && (
          <DropdownMenuItem onClick={() => navigate("/coach-log")} className="cursor-pointer">
            <ClipboardList className="me-2 h-4 w-4" />
            <span>Coach Log</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/docs")} className="cursor-pointer">
          <BookOpen className="me-2 h-4 w-4" />
          <span>{t('navigation.docs')}</span>
        </DropdownMenuItem>
        {showApps && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground font-normal py-1">
              Apps
            </DropdownMenuLabel>
            <DropdownMenuItem asChild className="cursor-pointer">
              <a
                href="https://club.gameon.futbol/sub-manager"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <img
                  src={subManagerLogo}
                  alt=""
                  aria-hidden="true"
                  className="me-2 h-4 w-4"
                />
                <span>Sub Manager</span>
                <ExternalLink className="ms-auto h-3 w-3 text-muted-foreground" />
              </a>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="me-2 h-4 w-4" />
          <span>{t('userMenu.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
